/**
 * Materiales usados durante un tratamiento: se marcan con un toque los que más se usan (cartuchos de anestesia, agujas, suturas, gasas…), se busca cualquier otro
 * del inventario y se escribe la cantidad. Lo usan la evolución del paciente y los procedimientos del plan.
 */
import { useEffect, useState } from "react";
import { api, puede, incluye } from "../api.ts";

export interface MaterialElegido { itemId: string; nombre: string; unidad: string; cantidad: number }
interface ItemInv { id: string; nombre: string; marca: string; especificacion: string; unidad: string; categoria: string; disponible: number; activo: boolean }
interface Inv { items: ItemInv[]; frecuentes: string[] }

let cache: { inv: Inv; hasta: number } | null = null;
const cargarInventario = async (): Promise<Inv> => {
  if (cache && cache.hasta > Date.now()) return cache.inv;
  const inv = await api<Inv>("GET", "/api/inventario");
  cache = { inv, hasta: Date.now() + 60_000 };
  return inv;
};
/** Para que el siguiente selector vea las cantidades ya actualizadas. */
export const olvidarInventario = () => { cache = null; };

const cant = (n: number) => String(Math.round(n * 100) / 100);

export function SelectorMateriales({ valor, alCambiar }: { valor: MaterialElegido[]; alCambiar: (x: MaterialElegido[]) => void }) {
  const [inv, setInv] = useState<Inv | null>(null);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  useEffect(() => { cargarInventario().then(setInv).catch(() => setError("No se pudo cargar el inventario.")); }, []);
  if (!puede("inventario:usar") || !incluye("inventario")) return null;
  if (error) return <p className="muted" style={{ margin: 0 }}>{error}</p>;
  if (!inv) return <p className="muted" style={{ margin: 0 }}>Cargando materiales…</p>;
  if (!inv.items.length) return <p className="muted" style={{ margin: 0 }}>Todavía no hay materiales en el inventario: la administración puede cargar la lista sugerida en Inventario.</p>;
  const activos = inv.items.filter((i) => i.activo);
  const porId = new Map(activos.map((i) => [i.id, i]));
  const frecuentes = inv.frecuentes.map((id) => porId.get(id)).filter((x): x is ItemInv => !!x);
  const elegido = (id: string) => valor.find((m) => m.itemId === id);
  const agregar = (i: ItemInv, n = 1) => {
    const ya = elegido(i.id);
    alCambiar(ya ? valor.map((m) => (m.itemId === i.id ? { ...m, cantidad: Math.round((m.cantidad + n) * 100) / 100 } : m)) : [...valor, { itemId: i.id, nombre: i.nombre, unidad: i.unidad, cantidad: n }]);
  };
  const buscados = q.trim().length >= 2 ? activos.filter((i) => `${i.nombre} ${i.marca} ${i.especificacion} ${i.categoria}`.toLowerCase().includes(q.trim().toLowerCase()) && !elegido(i.id)).slice(0, 8) : [];
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {frecuentes.length > 0 && (
        <div className="fila" style={{ gap: 4, flexWrap: "wrap", alignItems: "center" }}>
          <span className="muted" style={{ fontSize: 12 }}>Toca para sumar 1:</span>
          {frecuentes.map((i) => <button key={i.id} type="button" className="chip" style={{ cursor: "pointer" }} onClick={() => agregar(i)} title={`${i.nombre} · disponible ${cant(i.disponible)} ${i.unidad}`}>+ {i.nombre.replace(/\s*\(cartucho\)$/, "")}</button>)}
        </div>
      )}
      <input className="entrada" type="search" placeholder="Buscar otro material (guantes, gasas, sutura…)" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar material" />
      {buscados.length > 0 && <ul className="lista">{buscados.map((i) => <li key={i.id}><button type="button" onClick={() => { agregar(i); setQ(""); }}><b>{i.nombre}</b> <span className="muted">· {i.categoria} · hay {cant(i.disponible)} {i.unidad}</span></button></li>)}</ul>}
      {valor.length > 0 && (
        <ul className="lista">
          {valor.map((m) => {
            const i = porId.get(m.itemId);
            return (
              <li key={m.itemId} className="fila" style={{ justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span><b>{m.nombre}</b> <span className="muted">{i ? `· hay ${cant(i.disponible)} ${m.unidad}` : ""}</span>{i && m.cantidad > i.disponible && <span className="chip rojo" style={{ marginLeft: 6 }}>Más de lo que hay: anota la compra</span>}</span>
                <span className="fila" style={{ gap: 4, alignItems: "center" }}>
                  <button type="button" className="btn chico" onClick={() => alCambiar(m.cantidad <= 1 ? valor.filter((x) => x.itemId !== m.itemId) : valor.map((x) => (x.itemId === m.itemId ? { ...x, cantidad: Math.round((x.cantidad - 1) * 100) / 100 } : x)))} aria-label={`Quitar uno de ${m.nombre}`}>−</button>
                  <input className="entrada mono" inputMode="decimal" style={{ width: 64, textAlign: "center" }} value={m.cantidad} aria-label={`Cantidad de ${m.nombre}`}
                    onChange={(e) => { const n = Number(e.target.value.replace(",", ".")); alCambiar(valor.map((x) => (x.itemId === m.itemId ? { ...x, cantidad: Number.isFinite(n) && n >= 0 ? n : x.cantidad } : x))); }} />
                  <button type="button" className="btn chico" onClick={() => i && agregar(i)} aria-label={`Sumar uno de ${m.nombre}`}>+</button>
                  <button type="button" className="btn chico peligro" onClick={() => alCambiar(valor.filter((x) => x.itemId !== m.itemId))} aria-label={`Quitar ${m.nombre}`}>✕</button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Lo que se manda a la API (solo cantidades mayores que cero). */
export const materialesParaApi = (x: MaterialElegido[]) => x.filter((m) => m.cantidad > 0).map((m) => ({ itemId: m.itemId, cantidad: m.cantidad }));
